/**
 * <fitment-finder> — progressive enhancement over blocks/fitment-finder.liquid.
 *
 * The block renders the complete list (brand groups → series rows → model chips) with every <details>
 * open, the count line, the brand chips, the filter input, both no-match panels (hidden) and an inline
 * series index. Without JS all of that is visible and inert. This element adds:
 *   - brand chips (role=radiogroup): filter rows by data-brand, hide empty groups, update the count
 *   - the type-to-filter input (debounced 120 ms): rows whose data-search contains the normalised query
 *     stay, matching rows/model chips get .is-match, empty groups hide, the count reads "3 of 19"
 *   - no match: the query is looked up in the series index (inline JSON, or assets/series-index.json when
 *     the inline list is capped/empty) → the "redirect" panel (term/brand/url), else the "unknown" panel;
 *     the asset's default-locale URLs are re-rooted under data-locale-root (/sl/collections/…)
 *   - brand groups: always open ≥ 750 px; below, only the active chip's group (or the first) is open,
 *     every visible group opens while a query is active
 *   - the Pad Drawing pointer (deviation #106): its #pad-drawing link selects the gallery's drawing slide
 *     through the slideshow's own select({ id }) and brings the gallery into view, instead of the fragment jump
 * Plural forms come from the block (data-count-{one,two,few,other} / data-count-filtered-*, rendered
 * server-side with {n} / {shown} slots); the form is picked with Intl.PluralRules on <html lang>.
 * Nothing is fetched or rewritten via innerHTML; the element dispatches no events.
 */
const DESKTOP_QUERY = '(min-width: 750px)';
const FORMS = ['one', 'two', 'few', 'other'];

/** @param {unknown} value */
const normalise = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** @param {unknown} value */
const spaced = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

class FitmentFinder extends HTMLElement {
  /** @type {HTMLElement[]} */
  #rows = [];
  /** @type {HTMLElement[]} */
  #groups = [];
  /** @type {HTMLButtonElement[]} */
  #chips = [];
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLElement | null} */
  #count = null;
  /** @type {{ unknown: HTMLElement | null, redirect: HTMLElement | null }} */
  #panels = { unknown: null, redirect: null };
  #brand = '';
  #query = '';
  #rawQuery = '';
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Promise<Array<{ term: string, brand: string, url: string }>> | null} */
  #index = null;
  /** @type {MediaQueryList | null} */
  #media = null;
  /** @type {Intl.PluralRules | null} */
  #plural = null;
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.#rows = Array.from(this.querySelectorAll('.fitment-finder__row'));
    this.#groups = Array.from(this.querySelectorAll('.fitment-finder__brand'));
    this.#chips = Array.from(this.querySelectorAll('.fitment-finder__chip'));
    this.#input = this.querySelector('.fitment-finder__filter');
    this.#count = this.querySelector('.fitment-finder__count');
    this.#panels = {
      unknown: this.querySelector('[data-panel="unknown"]'),
      redirect: this.querySelector('[data-panel="redirect"]'),
    };

    try {
      this.#plural = new Intl.PluralRules(document.documentElement.lang || 'en');
    } catch {
      this.#plural = null;
    }

    for (const chip of this.#chips) {
      chip.addEventListener('click', () => this.#selectBrand(chip), { signal });
    }
    this.querySelector('.fitment-finder__chips')?.addEventListener('keydown', this.#handleChipKeydown, { signal });
    this.querySelector('[data-pad-drawing-link]')?.addEventListener('click', this.#showPadDrawing, { signal });

    if (this.#input) {
      this.#input.addEventListener(
        'input',
        () => {
          clearTimeout(this.#timer);
          this.#timer = setTimeout(() => this.#setQuery(this.#input?.value ?? ''), 120);
        },
        { signal }
      );
      // the native clear (x) button of type=search fires `search`, not always `input`
      this.#input.addEventListener('search', () => this.#setQuery(this.#input?.value ?? ''), { signal });
    }

    this.#media = window.matchMedia(DESKTOP_QUERY);
    this.#media.addEventListener('change', () => this.#applyOpenState(), { signal });

    const checked = this.#chips.find((chip) => chip.getAttribute('aria-checked') === 'true');
    this.#brand = checked?.dataset.brand ?? '';
    this.#syncChips();
    this.#apply();
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#timer);
  }

  /**
   * The pointer targets #pad-drawing, the gallery's last slide (snippets/product-media-gallery-content.liquid).
   * In the carousel, select it with the slideshow's API (the same call Horizon's variant picker makes) and scroll
   * the gallery into view — its scroll-margin (brand CSS §6) clears the sticky header. Anything else (grid
   * presentation, no slideshow) keeps the plain fragment jump.
   * @param {MouseEvent} event
   */
  #showPadDrawing = (event) => {
    const slide = document.getElementById('pad-drawing');
    const slideshow = /** @type {(HTMLElement & { select?: (input: { id: string }) => void }) | null | undefined} */ (
      slide?.closest('slideshow-component')
    );
    if (!slide || !slideshow || typeof slideshow.select !== 'function') return;

    event.preventDefault();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (slide.closest('media-gallery') ?? slideshow).scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    slideshow.select({ id: 'pad-drawing' });
  };

  /** @param {HTMLButtonElement} chip */
  #selectBrand(chip) {
    this.#brand = chip.dataset.brand ?? '';
    this.#syncChips();
    this.#apply();
  }

  #syncChips() {
    for (const chip of this.#chips) {
      const active = (chip.dataset.brand ?? '') === this.#brand;
      chip.setAttribute('aria-checked', active ? 'true' : 'false');
      chip.tabIndex = active ? 0 : -1;
    }
  }

  /** @param {KeyboardEvent} event */
  #handleChipKeydown = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = keys[/** @type {keyof typeof keys} */ (event.key)];
    if (!step || !this.#chips.length) return;
    const current = this.#chips.findIndex((chip) => chip === document.activeElement);
    const next = this.#chips[(current + step + this.#chips.length) % this.#chips.length];
    if (!next) return;
    event.preventDefault();
    next.focus();
    this.#selectBrand(next);
  };

  /** @param {string} value */
  #setQuery(value) {
    this.#rawQuery = spaced(value);
    this.#query = normalise(value);
    this.#apply();
  }

  #apply() {
    const query = this.#query;
    const raw = this.#rawQuery;
    let shown = 0;
    let total = 0;

    for (const row of this.#rows) {
      const weight = Number(row.dataset.calipers) || 1;
      const inBrand = !this.#brand || row.dataset.brand === this.#brand;
      if (inBrand) total += weight;

      const search = row.dataset.search ?? '';
      const matches = !query || search.includes(query) || (raw.length > 0 && search.includes(raw));
      const visible = inBrand && matches;

      row.hidden = !visible;
      row.classList.toggle('is-match', visible && query.length > 0);
      for (const model of row.querySelectorAll('.fitment-finder__model')) {
        const code = normalise(model.textContent);
        const hit = visible && query.length > 0 && code.length > 1 && (code.includes(query) || query.includes(code));
        model.classList.toggle('is-match', hit);
      }
      if (visible) shown += weight;
    }

    for (const group of this.#groups) {
      group.hidden = group.querySelector('.fitment-finder__row:not([hidden])') === null;
    }

    this.#applyOpenState();
    this.#renderCount(shown, total, query.length > 0);
    void this.#renderPanels(query.length > 0 && shown === 0);
  }

  #applyOpenState() {
    const desktop = this.#media?.matches ?? true;
    let first = true;
    for (const group of this.#groups) {
      const details = group.querySelector('details');
      if (!(details instanceof HTMLDetailsElement)) continue;
      if (desktop) {
        details.open = true;
        continue;
      }
      if (group.hidden) continue;
      details.open = Boolean(this.#query) || Boolean(this.#brand) || first;
      first = false;
    }
  }

  /** @param {number} n */
  #form(n) {
    let form = 'other';
    try {
      form = this.#plural?.select(n) ?? (n === 1 ? 'one' : 'other');
    } catch {
      form = n === 1 ? 'one' : 'other';
    }
    return FORMS.includes(form) ? form : 'other';
  }

  /**
   * @param {number} shown
   * @param {number} total
   * @param {boolean} filtered
   */
  #renderCount(shown, total, filtered) {
    const target = this.#count;
    if (!target) return;
    const form = this.#form(total);
    const key = (filtered ? 'countFiltered' : 'count') + form.charAt(0).toUpperCase() + form.slice(1);
    const fallbackKey = filtered ? 'countFilteredOther' : 'countOther';
    const template = target.dataset[key] ?? target.dataset[fallbackKey];
    target.textContent = template
      ? template.replace('{shown}', String(shown)).replace('{n}', String(total))
      : filtered
        ? `${shown} / ${total}`
        : String(total);
  }

  /** @param {boolean} noMatch */
  async #renderPanels(noMatch) {
    const { unknown, redirect } = this.#panels;
    if (!noMatch) {
      if (unknown) unknown.hidden = true;
      if (redirect) redirect.hidden = true;
      return;
    }

    const query = this.#query;
    let hit = null;
    if (redirect && query.length >= 3) {
      const index = await this.#loadIndex();
      if (query !== this.#query) return; // the query moved on while the index loaded
      hit = FitmentFinder.bestHit(index, query);
    }

    if (hit && redirect) {
      for (const slot of redirect.querySelectorAll('[data-term]')) slot.textContent = hit.term;
      for (const slot of redirect.querySelectorAll('[data-brand]')) slot.textContent = hit.brand;
      const link = redirect.querySelector('[data-redirect-link]');
      if (link instanceof HTMLAnchorElement) link.href = hit.url;
      redirect.hidden = false;
      if (unknown) unknown.hidden = true;
      return;
    }

    if (unknown) {
      for (const slot of unknown.querySelectorAll('[data-term]')) slot.textContent = this.#input?.value.trim() ?? '';
      unknown.hidden = false;
    }
    if (redirect) redirect.hidden = true;
  }

  /**
   * The inline index (blocks/fitment-finder.liquid) is complete only while ≤ 50 caliper_series entries exist:
   * Liquid iterates at most 50 of `.values`. When the block flags data-series-index-capped (or the inline
   * list is empty) the series loader's assets/series-index.json is fetched once — same [{term,brand,url}]
   * shape; a missing asset (404) counts as an empty index.
   * @returns {Promise<Array<{ term: string, brand: string, url: string }>>}
   */
  #loadIndex() {
    if (this.#index) return this.#index;
    this.#index = (async () => {
      let inline = [];
      try {
        inline = JSON.parse(this.querySelector('script[data-series-index]')?.textContent || '[]');
      } catch {
        inline = [];
      }
      if (!Array.isArray(inline)) inline = [];

      const capped = this.dataset.seriesIndexCapped === 'true';
      const url = this.dataset.seriesIndexUrl;
      if ((capped || inline.length === 0) && url) {
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data)) {
              const root = this.dataset.localeRoot || '/';
              return data.map((entry) =>
                entry && typeof entry === 'object' ? { ...entry, url: FitmentFinder.localise(entry.url, root) } : entry
              );
            }
          }
        } catch {
          // no series-index asset yet, or offline — the inline list stands
        }
      }
      return inline;
    })();
    return this.#index;
  }

  /**
   * assets/series-index.json is written once for every locale, so its URLs are default-locale paths
   * (/collections/brake-pads-…). Off the default locale they go under the page's locale root
   * (routes.root_url, "/sl"): /sl/collections/brake-pads-… answers with a 301 to the SL handle. The inline
   * index needs nothing — Liquid's collection URLs already carry the locale.
   * @param {unknown} url
   * @param {string} root
   */
  static localise(url, root) {
    if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) return url;
    const base = String(root || '/').replace(/\/+$/, '');
    if (!base || url === base || url.startsWith(`${base}/`)) return url;
    return `${base}${url}`;
  }

  /**
   * Longest series term contained in the query (or containing it), min 3 characters.
   * @param {Array<{ term: string, brand: string, url: string }>} index
   * @param {string} query normalised
   */
  static bestHit(index, query) {
    let best = null;
    let bestLength = 0;
    for (const entry of index) {
      if (!entry || typeof entry !== 'object' || !entry.url) continue;
      const term = normalise(entry.term);
      if (term.length < 3) continue;
      if (!(query.includes(term) || term.includes(query))) continue;
      if (term.length > bestLength) {
        best = entry;
        bestLength = term.length;
      }
    }
    return best;
  }
}

if (!customElements.get('fitment-finder')) {
  customElements.define('fitment-finder', FitmentFinder);
}
